import { Badge, Divider, List, ListItemButton, ListItemText, Stack, Tooltip, Typography, Box } from '@mui/material';
import type { CanvasSheetDescriptorModel } from '../services/api';

export const ALL_SHEETS_ID = '__all__';

type CanvasSheetNavigatorProps = {
  sheets: CanvasSheetDescriptorModel[];
  activeSheetId: string;
  onSelect: (sheetId: string) => void;
  dependencyDag?: Record<string, string[]>;
};

const CanvasSheetNavigator: React.FC<CanvasSheetNavigatorProps> = ({
  sheets,
  activeSheetId,
  onSelect,
  dependencyDag,
}) => {
  if (!sheets.length) {
    return null;
  }

  const buildTooltip = (sheetId: string): string | undefined => {
    const dependents =
      dependencyDag &&
      Object.entries(dependencyDag)
        .filter(([, sources]) => (sources ?? []).includes(sheetId))
        .map(([dependentId]) => dependentId);
    if (!dependents || dependents.length === 0) {
      return undefined;
    }
    return `Feeds ${dependents.length} downstream sheet${dependents.length > 1 ? 's' : ''}: ${dependents.join(', ')}`;
  };

  const sourceSheets = sheets.filter(s => s.source_type !== 'virtual');
  const virtualSheets = sheets.filter(s => s.source_type === 'virtual');

  const renderSheetList = (list: CanvasSheetDescriptorModel[], title: string) => {
        if (!list.length) return null;
        return (
            <Box mb={2}>
                <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ px: 1, display: 'block', mb: 0.5 }}>
                    {title}
                </Typography>
                <List dense disablePadding>
                    {list.map((sheet) => {
                        const isActive = activeSheetId === sheet.sheet_id;
                        const tooltip = buildTooltip(sheet.sheet_id);
                        const content = (
                            <ListItemButton
                                key={sheet.sheet_id}
                                selected={isActive}
                                onClick={() => onSelect(sheet.sheet_id)}
                                sx={{ 
                                    borderRadius: 1, 
                                    mb: 0.5, 
                                    pl: 1,
                                    '&.Mui-selected': { 
                                        bgcolor: 'primary.50', 
                                        color: 'primary.main',
                                        '& .MuiTypography-root': { color: 'primary.main' }
                                    } 
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" fontWeight={isActive ? 600 : 500} noWrap>
                                            {sheet.display_name || sheet.sheet_id}
                                        </Typography>
                                    }
                                    secondary={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {typeof sheet.row_count === 'number' && (
                                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
                                                    {sheet.row_count.toLocaleString()} rows
                                                </Typography>
                                            )}
                                        </Stack>
                                    }
                                />
                            </ListItemButton>
                        );
                        return tooltip ? (
                             <Tooltip key={sheet.sheet_id} title={tooltip} placement="right" arrow>{content}</Tooltip>
                        ) : content;
                    })}
                </List>
            </Box>
        );
    };

  return (
    <Stack sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
        Canvas Explorer
      </Typography>
      
      <ListItemButton
          selected={activeSheetId === ALL_SHEETS_ID}
          onClick={() => onSelect(ALL_SHEETS_ID)}
          sx={{ borderRadius: 1, mb: 2, bgcolor: activeSheetId === ALL_SHEETS_ID ? 'primary.50' : 'transparent' }}
        >
          <ListItemText primary="Full Graph" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        
        <Divider sx={{ mb: 2, borderColor: 'divider' }} />

        {renderSheetList(sourceSheets, "Source Tables")}
        {renderSheetList(virtualSheets, "Derived Views")}
    </Stack>
  );
};

export default CanvasSheetNavigator;


